import React, { useEffect, useState, useSyncExternalStore } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LiveTag, TeamAvatar, fmtDateTime } from "@/components/common";
import MatchStopwatch from "@/components/MatchStopwatch";
import { createLiveScore } from "@/lib/liveScore";
import { Minus, Plus, MapPin, Flag, RotateCcw } from "lucide-react";

export default function RefereeScoreboard({ match, code, allowResults, onWriting, onSaved, onFinal }) {
  const [controller] = useState(() => createLiveScore({
    initial: { score_a: match.score_a ?? 0, score_b: match.score_b ?? 0 },
    write: async (scores) => {
      onWriting();
      const response = await api.post(`/access/referee/matches/${match.id}/score`, { code, ...scores });
      return response.data;
    },
    onSaved,
  }));
  const scores = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  const [confirming, setConfirming] = useState(false);
  const [finishing, setFinishing] = useState(false);
  useEffect(() => {
    controller.acceptRemote({ score_a: match.score_a ?? 0, score_b: match.score_b ?? 0 });
  }, [controller, match.score_a, match.score_b]);

  const finish = async () => {
    if (scores.saving || scores.unsaved || scores.error) return;
    setFinishing(true);
    try {
      onWriting();
      await api.post(`/access/referee/matches/${match.id}/submit`, { code, score_a: scores.score_a, score_b: scores.score_b });
      try { localStorage.removeItem(`coachi-stopwatch-${match.id}`); } catch { /* Optional local timer. */ }
      toast.success("Final result submitted");
      await onFinal(match.id);
    } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
    finally { setFinishing(false); }
  };

  return (
    <Card className="overflow-hidden" data-testid="referee-live-scoreboard">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b p-4 text-xs text-muted-foreground">
        <LiveTag />
        <span>{fmtDateTime(match.scheduled_time, match.timezone)}</span>
        <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {match.field || "—"}</span>
      </div>
      <div className="grid grid-cols-2 divide-x">
        {[{ side: "a", team: match.team_a }, { side: "b", team: match.team_b }].map(({ side, team }) => (
          <section key={side} className="flex min-w-0 flex-col items-center gap-3 px-2 py-6 sm:px-4" aria-label={team?.name || `Team ${side}`}>
            <TeamAvatar team={team} size={48} />
            <h2 className="min-h-12 w-full break-words text-center font-heading text-lg font-bold">{team?.name || "TBD"}</h2>
            <div className="font-heading text-6xl font-black tabular-nums sm:text-7xl" aria-live="polite" data-testid={`referee-score-${side}`}>{scores[`score_${side}`]}</div>
            {allowResults && (
              <div className="flex gap-2">
                <Button variant="outline" size="icon" className="h-12 w-12" disabled={finishing || confirming || scores[`score_${side}`] === 0}
                  onClick={() => controller.change(`score_${side}`, -1)} aria-label={`Subtract point from ${team?.name}`} data-testid={`referee-${side}-minus`}><Minus className="h-6 w-6" /></Button>
                <Button size="icon" className="h-12 w-12" disabled={finishing || confirming}
                  onClick={() => controller.change(`score_${side}`, 1)} aria-label={`Add point to ${team?.name}`} data-testid={`referee-${side}-plus`}><Plus className="h-6 w-6" /></Button>
              </div>
            )}
          </section>
        ))}
      </div>
      {allowResults ? (
        <div className="space-y-2 px-4 pb-4">
          <div className="text-center text-xs text-muted-foreground" role="status">
            {scores.saving ? "Saving scores…" : scores.error ? "Scores not saved — retry before finishing." : "Scores saved automatically"}
          </div>
          {scores.error && <div className="rounded-lg border border-destructive/30 p-3 text-sm text-destructive">
            <p>{formatApiError(scores.error.response?.data?.detail) || "Could not save scores"}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => controller.retry()} disabled={scores.saving}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Retry save</Button>
          </div>}
          <Button variant="outline" className="w-full" onClick={() => setConfirming(true)} disabled={scores.saving || scores.unsaved || !!scores.error || finishing} data-testid="referee-finish-match"><Flag className="mr-1.5 h-4 w-4" /> Finish match</Button>
        </div>
      ) : <p className="px-4 pb-4 text-center text-xs text-muted-foreground">Score entry is managed by the host.</p>}
      <MatchStopwatch matchId={match.id} />
      <Dialog open={confirming} onOpenChange={(open) => !finishing && setConfirming(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm final result</DialogTitle>
            <DialogDescription>This result will be locked and cannot be changed. The stopwatch does not determine the final result.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 rounded-xl border p-4 text-center">
            <p className="font-semibold">{match.team_a?.name} vs {match.team_b?.name}</p>
            <p className="text-4xl font-black">{scores.score_a} : {scores.score_b}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={finishing} onClick={() => setConfirming(false)}>Back</Button>
            <Button disabled={finishing || scores.saving || scores.unsaved || !!scores.error} onClick={finish} data-testid="referee-confirm-final">{finishing ? "Submitting…" : "Submit final result"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
